import classNames from 'classnames'
import React from 'react'

import { getIconPath } from '@/common'
import { CustomItemLibrary } from '@/components/item/ItemList'
import Data from '@/data'
import { CraftingEmbed, CraftingEntry } from '@/types/embed'
import { Item } from '@/types/item'
import { faExternalLinkAlt, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ProfileItemNode } from 'embed/nodes'
import { itemBaseIcon, itemGetName } from 'logic/item'
import Store from 'store'
import { WithItemTooltip } from 'tooltip'
import Dialog from 'ui/dialog'
import Select, { SelectOption, underlinerFunc } from 'ui/select'
import Tooltip from 'ui/tooltip'
import { SmallIcon } from '../Icon'
import { skillItemTypes } from './SkillGems'

function CraftingItemEntry({
  item,
  active,
  onClick,
  onDelete
}: {
  item: CraftingEntry
  active?: boolean
  onClick: () => void
  onDelete: () => void
}) {
  const refItem = Store.useItem(item.itemId)
  const entry = (
    <li className={classNames('poe-crafting-entry', { 'poe-active': active })} onClick={onClick}>
      <span className="poe-type-icon" style={{ backgroundPositionX: `-${item.type}em` }} />
      <span className="poe-name">{item.name}</span>
      <span className="poe-remove" onClick={onDelete}>
        <FontAwesomeIcon icon={faTimes} />
      </span>
    </li>
  )
  if (refItem) {
    return <WithItemTooltip item={refItem}>{entry}</WithItemTooltip>
  } else {
    return <Tooltip tooltip={skillItemTypes[item.type] || skillItemTypes[0]}>{entry}</Tooltip>
  }
}

const craftTypeOptions = [
  { id: 'league-start', name: 'League Start' },
  { id: 'midgame', name: 'Midgame' },
  { id: 'endgame', name: 'Endgame' }
]
const priceRangeOptions = [
  { id: 'cheap', name: 'Cheap (1-5 Divines)' },
  { id: 'medium', name: 'Moderate (6-10 Divines)' },
  { id: 'expensive', name: 'Expensive (11-20 Divines)' },
  { id: 'exorbitant', name: 'Exorbitant (20+ Divines)' }
]

function CraftingItemEditor({
  item,
  onChange
}: {
  item: CraftingEntry
  onChange: (value: CraftingEntry | ((prev: CraftingEntry) => CraftingEntry)) => void
}) {
  const [showLib, setShowLib] = React.useState(false)
  const refItem = Store.useItem(item.itemId)

  const typeOptions = React.useMemo(() => skillItemTypes.map((t, i) => ({ id: i, name: t })), [])
  const onSetType = React.useCallback(
    (opt?: SelectOption<number>) => onChange((item) => ({ ...item, type: opt?.id || 0 })),
    [onChange]
  )
  const onSetName = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange((item) => ({ ...item, name: e.target.value })),
    [onChange]
  )
  const onSetSortKey = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange((item) => ({ ...item, sortKey: e.target.value })),
    [onChange]
  )
  const onSetComment = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange((item) => ({ ...item, comment: e.target.value })),
    [onChange]
  )
  const onSetLink = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange((item) => ({ ...item, link: e.target.value })),
    [onChange]
  )
  const onSetItem = React.useCallback(
    (id: number | undefined) => onChange((item) => ({ ...item, itemId: id })),
    [onChange]
  )
  // const onSetPrice = React.useCallback(value => onChange(item => ({...item, price: value})), [onChange]);
  const onSetCraftType = React.useCallback(
    (opt?: SelectOption<string>) => onChange((item) => ({ ...item, craftType: opt?.id })),
    [onChange]
  )
  const onSetPriceRange = React.useCallback(
    (opt?: SelectOption<string>) => onChange((item) => ({ ...item, priceRange: opt?.id })),
    [onChange]
  )

  if (!item) return null

  return (
    <>
      <Select
        options={typeOptions}
        value={typeOptions.find((opt) => opt.id === item.type)}
        onChange={onSetType}
        className="ui-Select"
      />
      <input type="text" value={item.name} onChange={onSetName} />
      <input type="text" value={item.sortKey ?? ''} onChange={onSetSortKey} placeholder="Sort key" />
      <input type="text" value={item.comment} onChange={onSetComment} placeholder="Comment" />
      <input type="text" value={item.link} onChange={onSetLink} placeholder="Article link" />
      {/* <label>Price (Divines): <NumberInput min={0} step={0.1} value={item.price} onChange={onSetPrice}/></label> */}
      <Select
        options={craftTypeOptions}
        value={craftTypeOptions.find((opt) => opt.id === item.craftType)}
        onChange={onSetCraftType}
        placeholder="Select craft type..."
        className="ui-Select"
      />
      <Select
        options={priceRangeOptions}
        value={priceRangeOptions.find((opt) => opt.id === item.priceRange)}
        onChange={onSetPriceRange}
        placeholder="Select price range..."
        className="ui-Select"
      />
      <div className="poe-item-ref" onClick={() => setShowLib(true)}>
        {refItem ? (
          <ProfileItemNode item={refItem} text={itemGetName(refItem)} />
        ) : (
          <span className="poe-empty">Example Item</span>
        )}
      </div>
      <CraftingItem item={item} />
      {!!showLib && (
        <Dialog className="poe-CraftingItemRef" title="Example Item" onClose={() => setShowLib(false)}>
          <CustomItemLibrary selected={item.itemId} onSelect={onSetItem} showEmpty />
        </Dialog>
      )}
    </>
  )
}

function CraftingItemIcon({ item }: { item: Item }) {
  const data = (item.unique && Data.uniques[item.unique]) || Data.items[item.base]
  const icon = data ? itemBaseIcon(data, item.map_tier) : undefined
  if (icon) {
    return <div className="poe-item-item" style={{ backgroundImage: `url(${getIconPath(icon)})` }} />
  }
}

const CraftingItemType = ({ type }: { type: number }) => (
  <div className="poe-item-type" style={{ backgroundPositionX: `-${type}em` }} />
)

const DivineOrb = () => <SmallIcon icon="Currency/CurrencyModValues" title="Divine Orb" />

export function CraftingItem({ item, search }: { item: CraftingEntry; search?: RegExp }) {
  const itemRef = Store.useItem(item.itemId)
  const HeaderType: 'a' | 'div' = item.link ? 'a' : 'div'
  const headerProps: {
    href?: string
    target?: string
    rel?: string
  } = {}
  if (item.link) {
    headerProps.href = new URL(item.link, window.location.href).href
    headerProps.target = '_blank'
    headerProps.rel = 'noopener noreferrer'
  }
  const content = (
    <HeaderType className="poe-CraftingItem" {...headerProps}>
      {itemRef ? <CraftingItemIcon item={itemRef} /> : <CraftingItemType type={item.type} />}
      <div className="poe-info">
        {!!item.link && (
          <div className="poe-link">
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </div>
        )}
        <div className="poe-name">{underlinerFunc(item.name, search)}</div>
        {!!item.price && (
          <div className="poe-price">
            {parseFloat(item.price.toFixed(1))} <DivineOrb />
          </div>
        )}
        <div className="poe-comment">{underlinerFunc(item.comment, search)}</div>
      </div>
    </HeaderType>
  )
  if (itemRef) {
    return <WithItemTooltip item={itemRef}>{content}</WithItemTooltip>
  } else {
    return content
  }
}

export default function CraftingEditor() {
  const [active, setActive] = React.useState(0)
  const embed = Store.useEmbed() as CraftingEmbed
  const dispatch = Store.useDispatch()
  const onCreate = React.useCallback(() => {
    setActive(embed.items.length)
    dispatch.setEmbed((embed) => {
      if (embed.type !== 'crafting') return embed
      embed = { ...embed }
      embed.items = embed.items.slice()
      embed.items.push({
        type: 0,
        name: 'New Item',
        comment: '',
        link: '',
        price: 0
      })
      return embed
    })
  }, [dispatch, embed.items.length])
  const onChange = React.useCallback(
    (value: CraftingEntry | ((prev: CraftingEntry) => CraftingEntry)) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'crafting') return embed
        const prev = embed.items[active]
        if (!prev) return embed
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return embed
        embed = { ...embed }
        embed.items = embed.items.slice()
        embed.items[active] = value
        return embed
      }),
    [dispatch, active]
  )
  const onDelete = React.useCallback(
    (index: number) =>
      dispatch.setEmbed((embed) => {
        if (embed.type !== 'crafting') return embed
        embed = { ...embed }
        embed.items = embed.items.slice()
        embed.items.splice(index, 1)
        return embed
      }),
    [dispatch]
  )
  return (
    <div className="poe-CraftingEditor">
      <ul className="poe-crafting-list">
        {embed.items.map((item, index) => (
          <CraftingItemEntry
            item={item}
            key={index}
            active={active === index}
            onClick={() => setActive(index)}
            onDelete={() => onDelete(index)}
          />
        ))}
        <li className="poe-crafting-entry poe-create" onClick={onCreate}>
          Create Item
        </li>
      </ul>
      <div className="poe-editor">
        {!!embed.items[active] && <CraftingItemEditor item={embed.items[active]} onChange={onChange} />}
      </div>
    </div>
  )
}
